/**
 * @graphql agg
 */
import gql from 'graphql-tag';
import { defineMessages, type MessageDescriptor, useIntl, type IntlShape } from 'react-intl-next';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import type { FC, RefObject } from 'react';
import React, { useContext, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { SHARE_AND_RESTRICT_DIALOG_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import type { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import type { PublicLinkPageProviderData } from '@confluence/public-links/entry-points/usePublicLinkPageDataProvider.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import type {
	FlagDescriptor,
	ShowFlagArgs,
} from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { GqlRestrictionsInput } from '@confluence/restrictions/entry-points/convertRestrictionsToGqlRestrictionsInput.ts';

import type {
	GeneralAccessLevel,
	Principal,
	PrincipalAndRole,
	SuccessFlagReason,
	ErrorFlagReason,
	UserPickerUserPrincipal,
} from '../controlled/commonTypes.tsx';
import { Dialogs, type DialogsProps } from '../controlled/Dialogs.tsx';
import type { HeaderProps } from '../controlled/shareAndRestrictDialog/header/Header.tsx';
import type { ShareAndRestrictDialogProps } from '../controlled/shareAndRestrictDialog/ShareAndRestrictDialog.tsx';
import type {
	ContentSubType,
	ContentType,
} from '../shareAndRestrictButton/ShareRestrictAndCopyCoordinator.tsx';

import type {
	ControllerDirectRestrictionsMutation as ControllerDirectRestrictionsMutation$data,
	ControllerDirectRestrictionsMutationVariables as ControllerDirectRestrictionsMutation$variables,
} from './__types__/ControllerDirectRestrictionsMutation.ts';
import { getActions, type Actions } from './actions.ts';
import { makeDefaultMasterState } from './masterState.ts';
import type { MasterState, DialogVariant } from './masterState.ts';
import { getDerivedState, type DerivedState } from './derivedState.ts';
import { makeDialogsProps } from './makeDialogsProps.ts';
import { useQueries } from './queries/useQueries.ts';
import type { PrincipalsMap } from './principalsMap.ts';
import { type TeardownController, useTeardownController } from './teardownController.ts';
import {
	getRestrictionsForPublishModal,
	getRestrictionsForMutation,
	getGqlRestrictionsInputForPublishModal,
} from './mutations/restrictionsTransformer.ts';
import type { PublishModalRestrictionsInput } from './queries/publishModalDataToQueriesState.ts';
import { useShareToPrincipals } from './mutations/useShareToPrincipals.ts';
import { getNotificationRecipients } from './mutations/notificationsTransformer.ts';
import { useCtaFlowController } from './ctaFlowController.ts';
import { useSetContentGeneralAccessMode } from './mutations/useSetContentGeneralAccessMode.ts';
import type { useSetContentGeneralAccessModeMutation$data as SetGeneralAccessMutationData } from './mutations/__generated__/useSetContentGeneralAccessModeMutation.graphql.ts';
import { useUpdateContentDirectRestrictions } from './mutations/useUpdateContentDirectRestrictions.ts';
import type { useUpdateContentDirectRestrictionsMutation$data as UpdateDirectRestrictionsData } from './mutations/__generated__/useUpdateContentDirectRestrictionsMutation.graphql.ts';
import { buildUpdateContentDirectRestrictionsFromStagedPrincipal } from './mutations/contentDirectRestrictionsPayload.ts';
import { generalAccessLevelToMode } from './generalAccessMappings.ts';
// eslint-disable-next-line import/no-cycle
import { useShouldShowSpotlight } from './queries/useSpotlightState.tsx';

const ControllerDirectRestrictionsMutation = gql`
	mutation ControllerDirectRestrictionsMutation(
		$pageId: ID!
		$restrictions: PageRestrictionsInput
	) {
		updatePage(input: { pageId: $pageId, restrictions: $restrictions }) {
			page {
				id
			}
		}
	}
`;

export const SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE = 'ShareAndRestrictDialog';

export const SAC_CONTENT_ADD_REMOVE_DOCUMENTATION =
	'https://support.atlassian.com/confluence-cloud/docs/add-or-remove-page-restrictions';
export const SAC_CONTENT_PERMS_MANAGEMENT_DOCUMENTATION =
	'https://support.atlassian.com/confluence-cloud/docs/manage-permissions-on-the-page-level/';

export type ContentRestrictionsQueryData = {
	generalAccessLevel: GeneralAccessLevel;
	principals: PrincipalAndRole[];
};

export type PermissionsSummary = {
	limit: number;
	remainingSlots: number;
	totalCount: number;
};

export type QueriesState = {
	contentRestrictions: ContentRestrictionsQueryData;
	numberOfAncestors: number;
	hasInheritedRestrictions: boolean;
	currentUser: Principal;
	publicLinkPageData: PublicLinkPageProviderData;
	spaceKey: string;
	parentPageLink: string | null | undefined;
	userCanEditContent: boolean;
	userCanEditRestrictions: boolean;
	edition: ConfluenceEdition;
	groupCount: number;
	teamCount: number;
	userCount: number;
	currentUserHasAccess: boolean;
	disableSharingToEmails: boolean;
	principalsPendingAccess: PrincipalAndRole[];
	shouldCurrentUserBeAddedToList?: boolean;
	permissionsSummary?: PermissionsSummary;
	trialEndDate: string | null;
};

export type StatesAndActions = {
	controllerProps: ControllerProps;
	controllerExtras: ControllerExtras;
	masterState: MasterState;
	derivedState: DerivedState;
	queriesState: QueriesState;
	actions: Actions;
	/** Context for Specific Access dialog background refetch (live-mode experiment only) */
	specificAccessDialogContext?: {
		backgroundRefetch: () => Promise<void>;
		isBackgroundRefetching: boolean;
	};
};

export type ContentRestrictionsMutator = (
	generalAccess: GeneralAccessLevel,
	principalsMap: PrincipalsMap,
	currentUser: Principal,
	includeInvites: boolean,
) => Promise<void>;

export type NotificationsSender = (
	stagedNotificationRecipients: Principal[],
	optionalMessage: string,
	currentUser: Principal,
) => Promise<void>;

/**
 * Extra data and functions provided to the main controller hook that don't
 * come from the parent
 */
export type ControllerExtras = {
	// Procedural interfaces for displaying flags, mutating data and sending
	// notifications would be in here.

	/** Raise a error toastie */
	errorFlag: (reason: ErrorFlagReason) => void;

	/** Raise a toastie */
	successFlag: (reason: SuccessFlagReason) => void;

	/** Raise a custom toastie */
	showFlag: (flag: Omit<FlagDescriptor, 'id'>) => Promise<FlagDescriptor>;

	/** Raise a custom error toastie */
	showErrorFlag: (flag: ShowFlagArgs) => Promise<FlagDescriptor>;

	/** Tell the BE to go send a notification */
	sendNotification: NotificationsSender;

	/** Tell the BE to mutate the content permissions */
	mutateContentRestrictions: ContentRestrictionsMutator;

	/** Tell the BE to mutate the general access mode */
	mutateGeneralAccess: (
		level: GeneralAccessLevel,
	) => Promise<{ data?: SetGeneralAccessMutationData }>;

	/** Tell the BE to mutate direct restrictions (Specific Access) */
	mutateRestrictionsAccess: (options: {
		principalsMap: PrincipalsMap;
		includeInvites: boolean;
	}) => Promise<{ data?: UpdateDirectRestrictionsData } | undefined>;

	/** Export restriction data for publish modal */
	exportPublishModalRestrictions: (
		generalAccess: GeneralAccessLevel,
		principalsMap: PrincipalsMap,
	) => void;

	/**
	 * Call the function to resolve mismatched restrictions
	 */
	callResolveMismatchedRestrictions: () => Promise<void>;

	teardownController: TeardownController;

	fireAnalyticEvent: ({
		eventType,
		action,
		actionSubject,
		actionSubjectId,
		attributes,
		source,
	}: {
		eventType: 'sendTrackEvent' | 'sendScreenEvent' | 'sendUIEvent' | 'sendOperationalEvent';
		action: string;
		actionSubject: string;
		actionSubjectId?: string;
		attributes?: any;
		source?: string;
	}) => void;

	experienceTracker: ExperienceTrackerAPI;

	formatMessage: IntlShape['formatMessage'];

	/**
	 * If the header spotlights are active
	 */
	isHeaderSpotlightActive: boolean;

	publicLinkButtonRef: RefObject<HTMLButtonElement>;
	shareToSlackButtonRef: RefObject<HTMLButtonElement>;

	/**
	 * Restrictions data from the query to check for mismatches between the queried restrictions and
	 * the staged restrictions as a result of deleted groups being filtered out
	 * Part of CONFCLOUD-80781 https://jira.atlassian.com/browse/CONFCLOUD-80781
	 */
	hasRestrictions: boolean;
	hasInheritedRestrictions: boolean;
	hasViewRestrictions: boolean;
	cloudId: string;
};

function useController(
	controllerProps: ControllerProps,
	controllerExtras: ControllerExtras,
): {
	dialogsProps: DialogsProps;
} {
	const { fireAnalyticEvent, cloudId } = controllerExtras;

	const {
		contentId,
		contentType,
		publishModalRestrictions,
		pushPublishModalRestrictions,
		spaceKey,
		onClose,
		onCloseComplete,
		initialDialog,
		hasRestrictions,
		isUnpublishedDraft,
	} = controllerProps;

	const [wasDialogClosedDuringLoading, setWasDialogClosedDuringLoading] = useState(false);
	const [masterState, setMasterState] = useState<MasterState>(
		makeDefaultMasterState(initialDialog),
	);
	const dispatchCtaFlowEvent = useCtaFlowController(setMasterState);
	const [shownLoadErrorFlag, setShownLoadErrorFlag] = useState(false);

	const queriesHookResponse = useQueries({
		contentId,
		contentType,
		publishModalRestrictions,
		spaceKey,
		cloudId,
	});

	if (
		queriesHookResponse.state === 'loading' ||
		// If the user closes the dialog in the loading state, then keep the loading
		// state showing even if the query finally resolves: it's not helpful to
		// replace the dialog contents with a flash of content during the exit
		// transition.
		wasDialogClosedDuringLoading
	) {
		// If we don't have the bare minimum needed to display the dialog, short-
		// circuit and give hardwired props for the loading state. By doing it this
		// way, the code that later calculates derivedState can rely on all the
		// inputs it needs being available, saving lots of guard code later on.

		const header: HeaderProps = {
			titleAndButtons: {
				title: initialDialog === 'accessRequests' ? 'accessRequests' : 'share',
				overflowMenu: undefined,
				onBackClick: initialDialog === 'accessRequests' ? () => {} : undefined,
				onCloseClick: () => {
					setWasDialogClosedDuringLoading(true);
				},
				// Needed for the AccessRequestsButton to start preloading early
				accessRequests: {
					lastTriggeredDialogVariant: null,
					openAccessRequestsDialog: () => {},
					clearElementToFocus: () => {},
					usdLoading: true,
				},
			},
			// principalsStager: undefined,
			// showDraftMessage: false,
			hasNavigationLockout: false,
			isPublishModalFlow: Boolean(pushPublishModalRestrictions),
		};

		const publishModalFlowHasSpecificAccess = Boolean(
			publishModalRestrictions?.some((operation) => operation?.restrictions.user.results.length),
		);
		const isPublishModalFlow = Boolean(pushPublishModalRestrictions);
		const usdLoadingHeightContext = {
			specificAccessVisible: hasRestrictions || publishModalFlowHasSpecificAccess,
			isDraft: isUnpublishedDraft,
			isPublishModalFlow,
			isEmbed: contentType === 'embed',
		};

		const shareAndRestrictDialog: ShareAndRestrictDialogProps = {
			onCloseRequest: () => {
				setWasDialogClosedDuringLoading(true);
			},
			onCloseComplete: () => {
				onClose();
				onCloseComplete?.();
			},
			header,
			body: undefined,
			footer: undefined,
			isLoading: true,
			analyticsAttributes: undefined,
			usdLoadingHeightContext,
		};

		return wasDialogClosedDuringLoading
			? { dialogsProps: {} }
			: { dialogsProps: { shareAndRestrictDialog } };
	}

	if (queriesHookResponse.state === 'error') {
		controllerExtras.experienceTracker.fail({
			name: SHARE_AND_RESTRICT_DIALOG_LOAD_EXPERIENCE,
			error: new Error('Failed to fetch share and restrictions dialog data'),
		});
		fireAnalyticEvent({
			eventType: 'sendTrackEvent',
			action: 'failed',
			actionSubject: 'query',
			actionSubjectId: 'ShareAndRestrictDialog',
		});
		if (!shownLoadErrorFlag) {
			setShownLoadErrorFlag(true);
			controllerExtras.errorFlag('loading-error');
		}
		return { dialogsProps: {} };
	}

	const { queriesState, refetchQueries, backgroundRefetch, isBackgroundRefetching } =
		queriesHookResponse;

	const derivedState = getDerivedState(masterState, queriesState, controllerProps);

	const actions = getActions(
		controllerProps,
		controllerExtras,
		masterState,
		derivedState,
		queriesState,
		setMasterState,
		dispatchCtaFlowEvent,
		refetchQueries,
	);

	const statesAndActions = {
		controllerProps,
		controllerExtras,
		masterState,
		derivedState,
		queriesState,
		actions,
		// Context for Specific Access dialog background refetch (experiment-only)
		specificAccessDialogContext: {
			backgroundRefetch,
			isBackgroundRefetching,
		},
	};

	return {
		dialogsProps: makeDialogsProps(statesAndActions),
	};
}

type ControllerComponentProps = {
	spaceKey?: string;
	contentId: string;
	contentType: ContentType;
	pageTitle: string | null | undefined;
	contentSubType?: ContentSubType;
	openFrom?: string;
	/**
	 * Some content types, such as embedded content and Company Hub pages, do not
	 * support restrictions. This flag allows the host to disable restrictions
	 * while still using the Share functionality.
	 */
	allowRestrictions: boolean;
	isUnpublishedDraft: boolean;
	pageMode: 'view' | 'edit';
	/**
	 * Input data for configuring restrictions within the publish modal.
	 * This defines the restrictions that will be applied during the publish operation.
	 */
	publishModalRestrictions?: PublishModalRestrictionsInput;

	/**
	 * Setting this property will open the DialogVariant of choice.
	 * Currently only Access Requests is fully wired to utilize it.
	 * */
	initialDialog?: DialogVariant;

	/** Callback to allow for unsetting the inital dialog property */
	onInitialDialogOpen?: () => void;

	/**
	 * A callback function used to update the publish modal restrictions.
	 * This function allows the host to modify or apply new restrictions to the publish modal.
	 */
	pushPublishModalRestrictions?: (_: PublishModalRestrictionsInput) => void;

	/**
	 * A callback function used to update the publish modal restrictions in GqlRestrictionsInput format.
	 * This allows direct use of GqlRestrictionsInput when publishing.
	 */
	pushPublishModalGqlRestrictionsInput?: (_: GqlRestrictionsInput) => void;

	/**
	 * A callback to inform the host that the share dialog session has concluded.
	 * The host would need to re-mount this controller to start a new one.
	 */
	onClose: () => void;

	/**
	 * The onCloseComplete() callback handles cleanup after the DialogStateContainer's showModal()
	 * completes, informing the host that the share dialog session has ended.
	 */
	onCloseComplete?: () => void;

	/**
	 * A callback to refetch the ShareAndRestrictButton's data that informs the icon.
	 */
	refetchButtonData: () => void;

	/**
	 * The booleans below show the current state of restrictions on a page,
	 * based on data returned from the query. These help detect mismatches
	 * between the actual restrictions and the staged state (e.g. due to
	 * deleted groups being filtered out).
	 *
	 * Part of CONFCLOUD-80781: https://jira.atlassian.com/browse/CONFCLOUD-80781
	 */

	/** Whether the page has any type of restrictions (view or edit). */
	hasRestrictions: boolean;
	/** Whether the page inherits restrictions from a parent page or space. */
	hasInheritedRestrictions: boolean;
	/** Whether the page has any view-specific restrictions. */
	hasViewRestrictions: boolean;
	isSuperAdmin: boolean;
	/**
	 * A ref to the element that should receive focus when the dialog opens.
	 */
	focusRef?: React.RefObject<HTMLElement>;

	/**
	 * A list of default suggested users for the Suggested User
	 */
	defaultSuggestedUsers?: UserPickerUserPrincipal[];
};

export type ControllerProps = ControllerComponentProps & WithFlagsProps;

const i18n = defineMessages({
	loadingErrorTitle: {
		id: 'share-and-restrict-dialog.general-access.flags.title.failed-to-load-title',
		defaultMessage: 'Access info failed to load',
		description: 'Flag title for a loading error causing a dialog to not open',
	},
	loadingErrorBody: {
		id: 'share-and-restrict-dialog.general-access.flags.title.failed-to-load-body',
		defaultMessage:
			'We couldn’t load some data so the access information may not be accurate. Try refreshing now.',
		description: 'Flag body text for a loading error causing a dialog to not open',
	},
	notificationErrorTitle: {
		id: 'share-and-restrict-dialog.general-access.flags.title.notification-error-title',
		defaultMessage: 'Notification failed to send',
		description: 'Flag title for a notification error',
	},
	notificationErrorBody: {
		id: 'share-and-restrict-dialog.general-access.flags.title.notification-error-body',
		defaultMessage:
			'We ran into a problem notifying the people you shared with. Try copying the link.',
		description: 'Flag body text for a notification error',
	},
	listTooLargeNotificationErrorTitle: {
		id: 'share-and-restrict-dialog.general-access.flags.title.list-too-large-notification-error-title',
		defaultMessage: 'Too many recipients',
		description: 'Flag title for a notification error when listis too large',
	},
	listTooLargeNotificationErrorBody: {
		id: 'share-and-restrict-dialog.general-access.flags.title.list-too-large-notification-error-body',
		defaultMessage:
			'You’re sharing with one or more distribution lists that contain too many people to share with at one time.',
		description: 'Flag body text for a notification error when list is too large',
	},
	mutationErrorTitle: {
		id: 'share-and-restrict-dialog.general-access.flags.title.mutation-error-title',
		defaultMessage: 'Access change failed',
		description: 'Flag title for a mutation error',
	},
	mutationErrorBody: {
		id: 'share-and-restrict-dialog.general-access.flags.title.mutation-error-body',
		defaultMessage: 'We ran into a problem completing your action. Wait a while and try again.',
		description: 'Flag body text for a mutation error',
	},
	notificationTitle: {
		id: 'share-and-restrict-dialog.flags.title.notification-sent',
		defaultMessage: 'Notification sent',
		description: 'Flag title for a notification that has been sent from a dialog',
	},
	alreadyHasAccessTitle: {
		id: 'share-and-restrict-dialog.flags.title.already-has-access',
		defaultMessage: 'The people added already have access',
		description:
			'Flag title indicating that users already have the necessary permissions to a dialog',
	},
	contentSharedTitle: {
		id: 'share-and-restrict-dialog.flags.title.content-shared',
		defaultMessage: 'Content shared',
		description: 'Flag title indicating content has been shared from a dialog',
	},
	draftSharedTitle: {
		id: 'share-and-restrict-dialog.flags.title.draft-shared',
		defaultMessage: 'Draft shared',
		description: 'Flag title indicating content has been shared from a dialog',
	},
	accessUpdatedTitle: {
		id: 'share-and-restrict-dialog.flags.title.access-updated',
		defaultMessage: 'Access updated',
		description: 'Flag title indicating access has been updated from a dialog',
	},
});
const flagMap: {
	[key: string]: [MessageDescriptor, MessageDescriptor];
} = {
	'loading-error': [i18n.loadingErrorTitle, i18n.loadingErrorBody],
	'notification-error': [i18n.notificationErrorTitle, i18n.notificationErrorBody],
	'list-too-large-error': [
		i18n.listTooLargeNotificationErrorTitle,
		i18n.listTooLargeNotificationErrorBody,
	],
	'mutation-error': [i18n.mutationErrorTitle, i18n.mutationErrorBody],
};

const successFlagMap = {
	'notification-sent': i18n.notificationTitle,
	'content-shared': i18n.contentSharedTitle,
	'draft-shared': i18n.draftSharedTitle,
	'access-updated': i18n.accessUpdatedTitle,
	'has-access-already': i18n.alreadyHasAccessTitle,
};

export const ControllerComponent: FC<ControllerComponentProps & WithFlagsProps> = (
	controllerProps,
) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { AutoDismissFlag } = useChoreographerAssets();
	const { flags } = controllerProps;
	const { showErrorFlag, showFlag } = flags;
	const {
		contentId,
		pushPublishModalRestrictions,
		pushPublishModalGqlRestrictionsInput,
		refetchButtonData,
		hasRestrictions,
		hasInheritedRestrictions,
		hasViewRestrictions,
	} = controllerProps;
	const { cloudId } = useSessionData();
	const { shareToPrincipals } = useShareToPrincipals(contentId);
	const [updateRestrictions] = useMutation<
		ControllerDirectRestrictionsMutation$data,
		ControllerDirectRestrictionsMutation$variables
	>(ControllerDirectRestrictionsMutation);
	const teardownController = useTeardownController(() => {
		controllerProps.onClose();
		controllerProps.onCloseComplete?.();
	});

	const { showSpotlight: isHeaderSpotlightActive } = useShouldShowSpotlight();
	const publicLinkButtonRef = useRef<HTMLButtonElement>(null);
	const shareToSlackButtonRef = useRef<HTMLButtonElement>(null);

	/**
	 * The functions that the Controller component can provide to the hook.
	 * Mutations, flags, fetch requests...
	 */
	const [commitGeneralAccess] = useSetContentGeneralAccessMode();
	const [commitDirectRestrictions] = useUpdateContentDirectRestrictions();

	const controllerExtras: ControllerExtras = {
		errorFlag: (reason) => {
			const [title, description] = flagMap[reason];
			void showErrorFlag({
				title: formatMessage(title),
				description: formatMessage(description),
			});
		},

		successFlag: (reason) => {
			const title: MessageDescriptor = successFlagMap[reason];
			void showFlag({
				type: 'success-circle',
				title: formatMessage(title),
				customComponent: (props: any) => (
					<AutoDismissFlag
						{...props}
						messageId={`share-and-restrict-dialog-success-flag-${reason}`}
						messageType="transactional"
					/>
				),
			});
		},

		showFlag,

		showErrorFlag,

		sendNotification: async (stagedNotificationRecipients, optionalMessage, currentUser) => {
			const notificationRecipients = getNotificationRecipients({
				stagedNotificationRecipients,
			});

			await shareToPrincipals({
				notificationRecipients,
				message: optionalMessage,
				sharerId: currentUser.id,
			});
		},

		callResolveMismatchedRestrictions: async () => {
			await updateRestrictions({
				variables: {
					pageId: contentId,
					restrictions: {
						read: { group: [], user: [] },
						update: { group: [], user: [] },
					},
				},
			});
			refetchButtonData();
		},

		mutateGeneralAccess: async (level) => {
			const mode = generalAccessLevelToMode(level);
			const result = await commitGeneralAccess({
				variables: {
					cloudId,
					input: { contentId, mode },
				},
			});
			refetchButtonData();
			return result;
		},

		mutateRestrictionsAccess: async ({ principalsMap, includeInvites }) => {
			const variables = buildUpdateContentDirectRestrictionsFromStagedPrincipal({
				cloudId,
				contentId,
				includeInvites,
				principalsMap,
				// For now, this will always be "add"
				// because this mutation is only triggered by the Save/Share CTA.
				action: 'add',
			});
			return commitDirectRestrictions({ variables });
		},

		mutateContentRestrictions: async (
			generalAccess,
			principalsMap,
			currentUser,
			includeInvites,
		) => {
			// if current users has can view permissions and has the ability to add restrictions
			// we will then grant them can edit permissions to prevent bugs example: https://product-fabric.atlassian.net/browse/USD-545
			if (generalAccess !== 'open-can-edit') {
				const currentUserInMap = principalsMap.get(currentUser.id);

				if (!currentUserInMap || currentUserInMap.role == 'can-view') {
					principalsMap.set(currentUser.id, {
						role: 'can-edit',
						principal: currentUser,
						isNewOrUpdatedPrincipal: false,
					});
				}
			}

			const restrictions = getRestrictionsForMutation({
				generalAccess,
				principals: principalsMap,
			});

			await updateRestrictions({
				variables: {
					pageId: contentId,
					restrictions: {
						...restrictions,
						includeInvites,
					},
				},
			});
			refetchButtonData();
		},

		exportPublishModalRestrictions: (generalAccess, principalsMap) => {
			const restrictions = getRestrictionsForPublishModal({
				generalAccess,
				principals: principalsMap,
			});

			pushPublishModalRestrictions!(restrictions);

			// Always push gqlRestrictionsInput when callback is available so that when
			// use_graphql_to_publish_pages_and_blogs experiment is cleaned up, we can
			// just remove restrictionsObj usages
			if (pushPublishModalGqlRestrictionsInput) {
				const gqlRestrictions = getGqlRestrictionsInputForPublishModal({
					generalAccess,
					principals: principalsMap,
				});
				pushPublishModalGqlRestrictionsInput(gqlRestrictions);
			}
		},

		teardownController,

		fireAnalyticEvent: ({
			eventType,
			action,
			actionSubject,
			actionSubjectId,
			attributes,
			source = SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
		}) => {
			createAnalyticsEvent({
				type: eventType,
				data: {
					source,
					action,
					actionSubject,
					actionSubjectId,
					attributes,
				},
			}).fire();
		},
		experienceTracker,
		formatMessage,
		isHeaderSpotlightActive,
		publicLinkButtonRef,
		shareToSlackButtonRef,
		hasRestrictions,
		hasInheritedRestrictions,
		hasViewRestrictions,
		cloudId,
	};

	const { dialogsProps } = useController(controllerProps, controllerExtras);
	return <Dialogs {...dialogsProps} />;
};

export const Controller = withFlags(ControllerComponent);
